import React, { createContext, useState, useContext, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import axios from '../utils/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        let token = localStorage.getItem('token');

        // No access token: try silent refresh via httpOnly cookie (covers long absences
        // where the 30d JWT expired but the 90d refresh cookie is still valid).
        if (!token) {
          try {
            const { data } = await axios.post(
              '/api/auth/refresh-token',
              {},
              { _skipAuthRefresh: true }
            );
            if (data?.token) {
              localStorage.setItem('token', data.token);
              token = data.token;
            }
          } catch (_) {
            setLoading(false);
            return;
          }
        }

        if (!token) {
          setLoading(false);
          return;
        }

        const { data: userData } = await axios.get('/api/auth/me');
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('token');
        setUser(null);
      }
      setLoading(false);
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      setUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      return user;
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const { data: updatedUser } = await axios.put('/api/auth/profile', profileData);

      if (updatedUser) {
        setUser(updatedUser);
        return updatedUser;
      }
      throw new Error('No user data received from server');
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.put('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateNewsletterPreference = async (subscribed) => {
    try {
      const { data: updatedUser } = await axios.put('/api/users/newsletter', { subscribed });
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    updateNewsletterPreference,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
